import { PlusIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ContentArea, { ContentNodeType } from "@/components/ContentArea";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import { Button } from "@/components/ui/button";
import useAutoScroll from "@/hooks/useAutoScroll";

export const AI_JUDGE_ASSERTION_PLACEHOLDER =
  "Example: The {output} is accurate, helpful, and directly answers the user’s question.";

export const AssertionVariableToken = ({ name }: { name: string }) => (
  <span className="input-variable inline p-1 text-xs transition-none">
    <code className="rounded bg-gray-100 font-mono">{`{${name}}`}</code>
  </span>
);

interface MultipleAssertionsProps {
  assertions: string[];
  onAssertionsChange: (assertions: string[]) => void;
  disabled?: boolean;
  assertionVariables?: string[];
  assertionPlaceholder?: string;
}

export const MultipleAssertions = ({
  assertions,
  onAssertionsChange,
  disabled = false,
  assertionVariables = [],
  assertionPlaceholder = AI_JUDGE_ASSERTION_PLACEHOLDER,
}: MultipleAssertionsProps) => {
  const normalizedAssertions = useMemo(
    () => (assertions.length > 0 ? assertions.map(assertion => String(assertion)) : [""]),
    [assertions],
  );
  const [localAssertions, setLocalAssertions] = useState<string[]>(() => normalizedAssertions);
  const ref = useRef<HTMLDivElement>(null);
  const isInitializedRef = useRef(false);
  const isReadonly = useIsReadonly();
  const readOnly = disabled || isReadonly;

  useEffect(() => {
    if (!isInitializedRef.current && normalizedAssertions.length > 0) {
      setLocalAssertions(normalizedAssertions);
      isInitializedRef.current = true;
    }
  }, [normalizedAssertions]);

  useEffect(() => {
    if (isInitializedRef.current) {
      const hasContent = localAssertions.some(assertion => assertion.trim() !== "");
      if (hasContent || localAssertions.length === 0) {
        onAssertionsChange(localAssertions);
      }
    }
  }, [localAssertions, onAssertionsChange]);

  const assertionItems = useMemo(
    () =>
      localAssertions.map((assertion, index) => ({ id: `assertion-${index}`, value: assertion })),
    [localAssertions],
  );
  const { lastItemRef, scrollContainerRef } = useAutoScroll(assertionItems, ref);

  const updateAssertion = useCallback((index: number, value: string) => {
    setLocalAssertions(previous => {
      const next = [...previous];
      next[index] = value;
      return next;
    });
  }, []);

  const removeAssertion = useCallback((index: number) => {
    setLocalAssertions(previous =>
      previous.length === 1 ? [""] : previous.filter((_, currentIndex) => currentIndex !== index),
    );
  }, []);

  const addAssertion = useCallback(() => {
    setLocalAssertions(previous => [...previous, ""]);
  }, []);

  return (
    <div className="flex w-full min-w-0 flex-col gap-y-2">
      <div
        ref={ref}
        className="max-h-[170px] w-full min-w-0 overflow-y-auto px-px"
        data-testid="assertion-list"
      >
        <div ref={scrollContainerRef} className="flex w-full flex-col gap-y-2">
          {localAssertions.map((assertion, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              ref={index === localAssertions.length - 1 ? lastItemRef : null}
              className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 py-px"
            >
              <div className="min-w-0 rounded-md border border-gray-200 bg-white px-3 py-2 transition focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-inset focus-within:ring-blue-500">
                <ContentArea
                  value={assertion}
                  onChange={event => updateAssertion(index, event.target.value)}
                  readOnly={readOnly}
                  hideContextMenu
                  markdownEnabled={false}
                  size="sm"
                  placeholder={assertionPlaceholder}
                  config={{
                    disabledNodes: [
                      ContentNodeType.Snippet,
                      ContentNodeType.DatasetExamples,
                      ContentNodeType.Tag,
                    ],
                    inputVariables: assertionVariables,
                  }}
                  className="min-h-[20px] border-0 bg-transparent p-0 font-mono text-sm leading-5 shadow-none outline-none focus-visible:ring-0"
                  containerClassName="assertion-content-area min-w-0 [&_.ProseMirror]:min-h-[20px] [&_.ProseMirror]:p-0 [&_.ProseMirror]:whitespace-pre-wrap [&_.ProseMirror]:break-words [&_.ProseMirror_p]:m-0"
                />
              </div>
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 text-gray-400 hover:text-gray-900"
                  onClick={() => removeAssertion(index)}
                  aria-label="Remove assertion"
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
      {!isReadonly && (
        <Button variant="ghostBlue" size="xs" onClick={addAssertion} disabled={disabled}>
          <PlusIcon className="mr-1 h-3 w-3" />
          Add assertion
        </Button>
      )}
    </div>
  );
};
